import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Collapse, Grid2, Typography } from "@mui/material";
import { addDays, format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { Virtuoso } from "react-virtuoso";

import Highlight from "../../../../../Highlight";
import {
  ScopeOfMovementColumn
} from "./scope-of-movement-column";
import {
  WorkerColumn
} from "./worker-column";
import {
  WorkerDayColumn
} from "./worker-day-column";
const BORDER_COLOR = "#DEDEDE";

type WorkerWorkcenterPatternGroupProps = {
  patternId: number;
  workers: {
    workerInfo: any;
    shiftsByDay: Record<string, any>; // keyed by yyyy-MM-dd
  }[];
  startingDay: Date;
  searchedTerm: string;
};

export const WorkerWorkcenterPatternGroup = (
  { startingDay, patternId, workers, searchedTerm }: any
) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(true);
  const patternName = workers?.[0]?.workerInfo?.from?.patternName ?? t("full-time-shifts_pattern");
  return (
    <Box key={patternId} width="100%">
      {/* Shift Pattern Header */}
      <Box
        sx={{
          position: "sticky",
          top: 108,
          zIndex: 98,
          backgroundColor: "#ECF3F9",
          borderTop: `1px solid ${BORDER_COLOR}`,
          paddingRight: "16px",
          py: 1,
          paddingLeft: "36px",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          gap: "12px",
          userSelect: "none",
        }}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center" }}>
          {open ? (
            <KeyboardArrowUpIcon sx={{ width: 24, height: 24 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ width: 24, height: 24 }} />
          )}
        </Box>
        <Box sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography
            sx={{
              fontFamily: "Roboto",
              color: "rgba(0,0,0,0.87)",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "21px",
            }}
          >
            <Highlight value={patternName} searchText={searchedTerm} />
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: "28px",
              color: "rgba(0,0,0,0.87)",
              marginLeft: "8px",
            }}
          >
            {workers.length !== 1
              ? t("full-time-shifts_worker-count-other", { count: workers.length })
              : t("full-time-shifts_worker-count-one", { count: workers.length })}
          </Typography>
        </Box>
      </Box>

      {/* Worker rows */}
      <Collapse in={open} timeout={200} unmountOnExit>
        <Box sx={{
          width: "100%",
        }}>
          <Virtuoso
            style={{
              height: 117*workers.length,
              width: "100%",
              overflow: "none",
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // Internet Explorer + Edge
          }}
            totalCount={workers.length}
            itemSize={() => 116}
            itemContent={(index) => {
              const {workerInfo, shiftsByDay} = workers[index];
              return (
                <Grid2
                  container
                  key={workerInfo.workerId ?? `worker-${index}`}
                  sx={{
                    display: "flex",
                    width: "100%",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  <WorkerColumn reassignment={workerInfo} searchedTerm={searchedTerm} />
                  <ScopeOfMovementColumn reassignment={workerInfo} />
                  {Array.from({ length: 7 }).map((_, dayIndex) => {
                    const date = addDays(startingDay, dayIndex);
                    const dateKey = format(date, "yyyy-MM-dd");
                    const shift = shiftsByDay[dateKey] || [];
                    return <Box
                      key={`${dayIndex}-${workerInfo.workerId}`}
                      sx={{
                        minWidth: "161px",
                        width: "100%",
                        borderRight: dayIndex !== 6 ? `1px solid ${BORDER_COLOR}` : "none",
                      }}
                    >
                      {shift && (
                        <WorkerDayColumn
                          reassignment={shift}
                          date={addDays(startingDay, dayIndex)}
                        />
                      )}
                    </Box>
                  })}
                </Grid2>
              );
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}